import { useEffect } from "react";
import { createPortal } from "react-dom";
import L from "../../../constants/baysportLabels";
import { calendarEntryTitle } from "../../../constants/calendarSessionTypes";
import ClientSearchPicker from "../common/ClientSearchPicker";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import { Field, Input, Select } from "../../ui";
import SessionDateTimeFields from "./SessionDateTimeFields";
import CompetitionDetailsFields from "./CompetitionDetailsFields";

export default function CalendarCreateSheet({
  open,
  kind,
  form,
  onChange,
  groups,
  branches,
  doctors,
  saving,
  coverPreviewUrl,
  onCoverFileSelect,
  onClose,
  onSubmit,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || !kind) return null;

  const title = calendarEntryTitle(kind);
  const isCompetition = kind === "event" && form.event_kind === "competition";

  return createPortal(
    <div className={aa.calSheetBackdrop} onClick={onClose} role="presentation">
      <div
        className={`${aa.calCreateSheet} ${isCompetition ? aa.calCreateSheetCompetition : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className={aa.calSheetHandle} aria-hidden />
        {isCompetition ? (
          <div className={aa.calCreateHero}>
            <div className={aa.calCreateHeroGlow} aria-hidden />
            <span className={aa.calCreateHeroTag}>Соревнование</span>
            <p className={aa.calCreateHeroText}>
              Клиенты увидят событие в календаре и смогут подать заявку
            </p>
          </div>
        ) : null}

        <header className={aa.calCreateHead}>
          <h2 className={aa.calCreateTitle}>{title}</h2>
          <button
            type="button"
            className={aa.calSheetClose}
            onClick={onClose}
            aria-label="Закрыть"
          >
            <svg viewBox="0 0 24 24" aria-hidden>
              <path
                d="M6 6l12 12M18 6 6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <form
          id="cal-create-sheet-form"
          className={aa.calCreateForm}
          onSubmit={onSubmit}
        >
          {kind === "event" ? (
            <div className={aa.calCreateSection}>
              <Field label="Название">
                <Input
                  value={form.title}
                  onChange={(e) => onChange({ title: e.target.value })}
                  required
                  placeholder="Название события"
                />
              </Field>
              <Field label="Тип события">
                <Select
                  value={form.event_kind}
                  onChange={(e) => onChange({ event_kind: e.target.value })}
                >
                  <option value="competition">Соревнование</option>
                  <option value="meeting">Собрание</option>
                  <option value="other">Другое</option>
                </Select>
              </Field>
            </div>
          ) : null}

          {isCompetition ? (
            <CompetitionDetailsFields
              form={form}
              onChange={onChange}
              coverPreviewUrl={coverPreviewUrl}
              onCoverFileSelect={onCoverFileSelect}
            />
          ) : null}

          {kind === "individual" ? (
            <div className={aa.calCreateSection}>
              <Field label="Клиент">
                <ClientSearchPicker
                  value={form.patient_id}
                  onChange={(patient_id) => onChange({ patient_id })}
                  placeholder="Имя или телефон"
                />
              </Field>
            </div>
          ) : null}

          {kind === "training" ? (
            <div className={aa.calCreateSection}>
              <Field label={L.group}>
                <Select
                  value={form.specialization_id}
                  onChange={(e) =>
                    onChange({ specialization_id: e.target.value })
                  }
                  required
                >
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          ) : null}

          <div className={aa.calCreateSection}>
            <p className={aa.calCreateSectionTitle}>Время и ответственный</p>
            <SessionDateTimeFields
              className={s.calCreateSheetSchedule}
              date={form.sessionDate}
              startTime={form.startTime}
              endTime={form.endTime}
              onChange={(patch) => onChange(patch)}
            />
            <Field label={L.trainer}>
              <Select
                value={form.doctor_id}
                onChange={(e) => onChange({ doctor_id: e.target.value })}
                required
              >
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.user?.full_name || d.full_name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          {!(kind === "event" && form.event_kind === "competition") ? (
            <div className={aa.calCreateSection}>
              <Field label="Зал">
                <Select
                  value={form.branch_id}
                  onChange={(e) => onChange({ branch_id: e.target.value })}
                >
                  <option value="">Не указан</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          ) : null}

          <div className={aa.calCreateSection}>
            <Field label="Заметка для администратора">
              <Input
                value={form.notes}
                onChange={(e) => onChange({ notes: e.target.value })}
                placeholder="Необязательно"
              />
            </Field>
          </div>
        </form>

        <footer className={aa.calCreateFoot}>
          <button type="button" className={aa.calCreateCancel} onClick={onClose}>
            Отмена
          </button>
          <button
            type="submit"
            form="cal-create-sheet-form"
            className={`${aa.calFilterApply} ${isCompetition ? aa.calCreateSubmitComp : ""}`}
            disabled={saving}
          >
            {saving ? "Сохранение…" : "Создать"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
